<script context="module" lang="ts">
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';
	import Media from '$lib/components/media/Media.svelte';
	import Section from '$lib/components/layout/Section.svelte';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';

	export const config: SectionConfig = {
		collection: 'section_page_highlight',
		availableFor: ['pages', 'special-pages'],
		query: gql`
		internal_name
		display_type
		translations {
			languages_code {
				code
			}
			title
		}
		items {
			section_page_highlight_item_id {
				translations {
					languages_code {
						code
					}
					title
					text
				}
				preview_media {
					...Media
				}
				icon {
					...Media
				}
				link {
					...Link
				}
			}
		}
	`
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		title: string;
		display_type: string;
		items: {
			section_page_highlight_item_id: {
				title: string;
				text: string;
				preview_media: any;
				icon: any;
				link: any;
			};
		}[];
	};
</script>

<script lang="ts">
	import TextWithIcon from '$lib/components/util/TextWithIcon.svelte';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)
</script>

<Section gapType="padding">
	<div class="title {data.display_type}">
		{data.title}
	</div>
	<div class="wrapper {data.display_type}">
		{#each data.items as { section_page_highlight_item_id: item }}
			<div class="container">
				<DirectusLink {...item.link}>
					<div class="preview-media">
						<Media {...item.preview_media} fit="cover" />
					</div>
				</DirectusLink>
				<div class="content">
					{#if item.icon}
						<div class="icon-wrapper">
							<Media {...item.icon} />
						</div>
					{/if}
					<div class="text-wrapper">
						<div class="page-title">
							{item.title}
						</div>
						<div class="divider" />
						<div class="page-text">
							{item.text}
						</div>
						<div class="divider" />
						<div class="page-link">
							<DirectusLink {...item.link}>
								<TextWithIcon {...item.link} />
							</DirectusLink>
						</div>
					</div>
				</div>
			</div>
		{/each}
	</div>
</Section>

<style lang="scss">
	.wrapper {
		&.three-cards {
			display: grid;
			grid-template-columns: repeat(3, minmax(0, 1fr));
			gap: v(content-gap-lg);
			@media (max-width: 600px) {
				grid-template-columns: 1fr;
			}
			.icon-wrapper {
				display: none;
			}
			.content {
				display: flex;
			}
		}
		&.two-cards {
			display: grid;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: v(content-gap-lg);
			@media (max-width: 600px) {
				grid-template-columns: 1fr;
			}

			.container {
				display: flex;
				flex-direction: column;

				.content {
					flex: 1;

					.text-wrapper {
						justify-content: center;
					}
				}
			}
		}
		&.single-card {
			display: flex;
			.container {
				display: grid;
				grid-template-columns: repeat(2, minmax(0, 1fr));
				@media (max-width: 900px) {
					grid-template-columns: 1fr;
				}
			}
			.content {
				padding: v(content-padding-sm);
				align-items: center;
				padding-inline-start: v(content-padding-xl);
				display: flex;

				@include media(phone) {
					padding: v(content-padding-sm);
				}
			}
			.preview-media {
				position: relative;
				width: 100%;
				height: 100%;
				aspect-ratio: 3/1;
				// min-height: 400px;
				:global(img) {
					position: absolute;
					height: 100%;
					width: 100%;
					overflow: hidden;
				}
				@include media(phone) {
					aspect-ratio: 2 /1;
				}
			}

			.text-wrapper {
				@media (min-width: 1201px) {
					border-inline-start: solid 1px color(black);
					padding-inline-start: v(content-gap-lg);
				}
			}

			.icon-wrapper {
				border-inline-end: 0;
				padding-inline-end: 0;
				max-width: 6rem;
				width: 100%;
				:global(svg) {
					width: 100%;
				}
			}
		}
		@media (max-width: 1200px) {
			.icon-wrapper {
				display: none;
			}
			.content {
				display: flex;
				width: 100%;
				padding: v(content-padding-sm);
			}
			.page-title {
				height: 100%;
			}
		}
		@include media(phone) {
			.content {
				padding: v(content-padding-sm);
			}
		}
	}
	.title {
		@include font(h3);
		margin-bottom: v(content-padding-lg);
		&.three-cards {
			text-align: center;
		}
	}
	.content {
		display: grid;
		grid-template-columns: 1fr 4fr;
		padding: v(content-padding);
		gap: v(content-gap-xl);
		background: color(white);
		border-radius: v(border-radius);
		@include media(tablet) {
			padding: v(content-gap);
		}
	}
	.preview-media {
		border-radius: v(border-radius);
		overflow: hidden;
		@include media(tablet) {
			aspect-ratio: 17/9;
			overflow: visible;
		}
		:global(img),
		:global(video) {
			transition: all 0.3s ease;
		}
		&:hover {
			:global(img),
			:global(video) {
				transform: scale(1.06);
				@include media(tablet) {
					transform: none;
				}
			}
		}
	}
	.icon-wrapper {
		border-inline-end: solid 1px color(black);
		padding-inline-end: v(content-gap-lg);
		display: flex;
		align-items: center;
	}
	.page-title {
		@include font(h4);
	}
	.text-wrapper {
		display: flex;
		flex-direction: column;
		gap: v(content-gap-sm);
		width: 100%;
		@media (max-width: 600px) {
			gap: v(content-gap-sm);
		}
	}
	.divider {
		width: 100%;
		border-bottom: solid color(black) 1px;
	}
</style>
